import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, ArrowDownToLine, Bot, Lightbulb, PencilLine } from 'lucide-react';

interface MessageStatsCounterProps {
  totalCount?: number;
  customerCount?: number;
  aiAutoCount?: number;
  aiAssistCount?: number;
  manualCount?: number;
}

// N-16: each count used to be an icon and a bare number ("💬 8 | ↓ 6 | 🚗 2" in the
// audit's words), which an operator has to decode. Every count now carries its own
// translated word, so the row reads on its own without guessing what an icon means.
export function MessageStatsCounter({
  totalCount = 0,
  customerCount = 0,
  aiAutoCount = 0,
  aiAssistCount = 0,
  manualCount = 0
}: MessageStatsCounterProps) {
  const { t } = useTranslation('conversations');

  // Don't render if no messages at all
  if (totalCount === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {/* Total Messages */}
      <span className="inline-flex items-center gap-1">
        <MessageSquare className="h-3 w-3" />
        {t('messageStats.total', { count: totalCount })}
      </span>

      {/* Customer Messages */}
      {customerCount > 0 && (
        <span className="inline-flex items-center gap-1">
          <ArrowDownToLine className="h-3 w-3 text-gray-500" />
          {t('messageStats.received', { count: customerCount })}
        </span>
      )}

      {/* AI Auto Messages */}
      {aiAutoCount > 0 && (
        <span className="inline-flex items-center gap-1">
          <Bot className="h-3 w-3 text-green-500" />
          {t('messageStats.aiAuto', { count: aiAutoCount })}
        </span>
      )}

      {/* AI Assist Messages */}
      {aiAssistCount > 0 && (
        <span className="inline-flex items-center gap-1">
          <Lightbulb className="h-3 w-3 text-blue-500" />
          {t('messageStats.aiAssist', { count: aiAssistCount })}
        </span>
      )}

      {/* Manual Messages */}
      {manualCount > 0 && (
        <span className="inline-flex items-center gap-1">
          <PencilLine className="h-3 w-3 text-purple-500" />
          {t('messageStats.manual', { count: manualCount })}
        </span>
      )}
    </div>
  );
}
